import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import type { Team, TeamMember, TenantMember } from '@shared/types/teams';
import { SalesRoleField } from '@/components/settings/SalesRoleField';

export const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');

  const [tenantMembers, setTenantMembers] = useState<TenantMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  const [displayName, setDisplayName] = useState('');
  const [savingDisplayName, setSavingDisplayName] = useState(false);

  const [newTeamName, setNewTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);

  const [renamingTeamId, setRenamingTeamId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const [addMemberUserByTeam, setAddMemberUserByTeam] = useState<Record<string, string>>({});

  const myTenantMember = useMemo(
    () => tenantMembers.find((member) => member.user_id === currentUserId) ?? null,
    [tenantMembers, currentUserId],
  );

  const isTenantManager = myTenantMember?.tenant_role === 'manager';

  const memberByUserId = useMemo(() => {
    const map: Record<string, TenantMember> = {};
    for (const member of tenantMembers) {
      map[member.user_id] = member;
    }
    return map;
  }, [tenantMembers]);

  const membersByTeamId = useMemo(() => {
    const map: Record<string, TeamMember[]> = {};
    for (const member of teamMembers) {
      const list = map[member.team_id] ?? [];
      list.push(member);
      map[member.team_id] = list;
    }
    return map;
  }, [teamMembers]);

  const refreshData = async () => {
    setError(null);
    const { data: authData, error: authError } = await supabase.auth.getUser();

    if (authError || !authData.user) {
      setError(authError?.message ?? 'Utilisateur non authentifié');
      setLoading(false);
      return;
    }

    const userId = authData.user.id;
    setCurrentUserId(userId);
    setEmail(authData.user.email ?? null);

    const [tenantMembersRes, teamsRes, teamMembersRes] = await Promise.all([
      supabase.from('tenant_members').select('id, tenant_id, user_id, display_name, tenant_role, sales_role, sales_role_detail, created_at, updated_at').order('display_name'),
      supabase.from('teams').select('id, tenant_id, name, created_by, archived_at, created_at, updated_at').is('archived_at', null).order('name'),
      supabase.from('team_members').select('id, team_id, user_id, role, created_at, updated_at'),
    ]);

    if (tenantMembersRes.error) {
      setError(tenantMembersRes.error.message);
      setLoading(false);
      return;
    }

    if (teamsRes.error) {
      setError(teamsRes.error.message);
      setLoading(false);
      return;
    }

    if (teamMembersRes.error) {
      setError(teamMembersRes.error.message);
      setLoading(false);
      return;
    }

    const tenantRows = (tenantMembersRes.data ?? []) as TenantMember[];
    const myRow = tenantRows.find((member) => member.user_id === userId);

    setTenantMembers(tenantRows);
    setTeams((teamsRes.data ?? []) as Team[]);
    setTeamMembers((teamMembersRes.data ?? []) as TeamMember[]);
    setDisplayName(myRow?.display_name ?? '');
    setLoading(false);
  };

  useEffect(() => {
    void refreshData();
  }, []);

  const saveDisplayName = async () => {
    if (!currentUserId) return;
    setSavingDisplayName(true);
    setError(null);

    const { error: updateError } = await supabase
      .from('tenant_members')
      .update({ display_name: displayName.trim() || null })
      .eq('user_id', currentUserId);

    setSavingDisplayName(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    await refreshData();
  };

  const createTeam = async () => {
    if (!currentUserId || !newTeamName.trim()) return;

    setCreatingTeam(true);
    setError(null);

    const { error: createError } = await supabase
      .from('teams')
      .insert({ name: newTeamName.trim(), created_by: currentUserId });

    setCreatingTeam(false);

    if (createError) {
      setError(createError.message);
      return;
    }

    setNewTeamName('');
    await refreshData();
  };

  const renameTeam = async (teamId: string) => {
    if (!renameValue.trim()) return;

    setError(null);
    const { error: renameError } = await supabase
      .from('teams')
      .update({ name: renameValue.trim() })
      .eq('id', teamId);

    if (renameError) {
      setError(renameError.message);
      return;
    }

    setRenamingTeamId(null);
    setRenameValue('');
    await refreshData();
  };

  const archiveTeam = async (teamId: string) => {
    setError(null);
    const { error: archiveError } = await supabase
      .from('teams')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', teamId);

    if (archiveError) {
      setError(archiveError.message);
      return;
    }

    await refreshData();
  };

  const addMember = async (teamId: string) => {
    const userId = addMemberUserByTeam[teamId];
    if (!userId) return;

    setError(null);
    const { error: addError } = await supabase
      .from('team_members')
      .insert({ team_id: teamId, user_id: userId, role: 'member' });

    if (addError) {
      setError(addError.message);
      return;
    }

    setAddMemberUserByTeam((previous) => ({ ...previous, [teamId]: '' }));
    await refreshData();
  };

  const removeMember = async (teamMember: TeamMember) => {
    const teamMemberList = membersByTeamId[teamMember.team_id] ?? [];
    const managerCount = teamMemberList.filter((entry) => entry.role === 'manager').length;

    if (teamMember.role === 'manager' && managerCount <= 1) {
      setError('Impossible de retirer le dernier responsable de l’équipe.');
      return;
    }

    setError(null);
    const { error: removeError } = await supabase
      .from('team_members')
      .delete()
      .eq('id', teamMember.id);

    if (removeError) {
      setError(removeError.message);
      return;
    }

    await refreshData();
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="mb-8">
        <h1 className="font-display text-2xl font-bold text-gray-900">Réglages</h1>
        <Link to="/connections" className="mt-3 inline-flex text-sm font-semibold text-domino-aubergine underline underline-offset-4">Gérer les connexions et les sources</Link>
        {isTenantManager && (
          <Link to="/settings/usage" className="ml-4 mt-3 inline-flex text-sm font-semibold text-domino-aubergine underline underline-offset-4">Consommation</Link>
        )}
        <p className="text-sm text-gray-500">Profil et gouvernance des équipes</p>
      </div>

      {error && (
        <Card className="mb-4 border-red-200 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </Card>
      )}

      <Card className="mb-6">
        <h2 className="mb-3 text-sm font-semibold text-gray-900">Profil</h2>
        <p className="text-sm text-gray-600">{email || 'Email indisponible'}</p>

        <div className="mt-4 max-w-sm space-y-2">
          <label className="block text-xs font-medium text-gray-600">Nom affiché</label>
          <Input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Ex: Julie Martin"
          />
          <Button onClick={saveDisplayName} loading={savingDisplayName}>
            Enregistrer
          </Button>
        </div>

        {currentUserId && (
          <SalesRoleField
            userId={currentUserId}
            role={myTenantMember?.sales_role ?? null}
            detail={myTenantMember?.sales_role_detail ?? null}
          />
        )}

        <div className="mt-4">
          <Badge variant={isTenantManager ? 'success' : 'secondary'}>
            {isTenantManager ? 'Gestion de l’espace' : 'Membre de l’espace'}
          </Badge>
        </div>
      </Card>

      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Équipes</h2>
            <p className="text-xs text-gray-500">
              Les applications partagées utilisent ces équipes pour gérer les accès.
            </p>
          </div>
        </div>

        {!isTenantManager && (
          <p className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
            Seuls les responsables de l’espace peuvent modifier les équipes. Vous pouvez consulter celles dont vous êtes membre.
          </p>
        )}

        {isTenantManager && (
          <div className="mb-6 flex max-w-lg items-center gap-2">
            <Input
              value={newTeamName}
              onChange={(event) => setNewTeamName(event.target.value)}
              placeholder="Nom de l’équipe"
            />
            <Button onClick={createTeam} loading={creatingTeam}>Créer</Button>
          </div>
        )}

        {teams.length === 0 ? (
          <p className="text-sm text-gray-500">Aucune équipe.</p>
        ) : (
          <div className="space-y-4">
            {teams.map((team) => {
              const teamMemberList = (membersByTeamId[team.id] ?? []).slice().sort((a, b) => {
                if (a.role !== b.role) return a.role === 'manager' ? -1 : 1;
                return a.user_id.localeCompare(b.user_id);
              });

              const availableUsers = tenantMembers.filter((member) =>
                !teamMemberList.some((entry) => entry.user_id === member.user_id));

              return (
                <div key={team.id} className="rounded-xl border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {renamingTeamId === team.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={renameValue}
                          onChange={(event) => setRenameValue(event.target.value)}
                          className="w-64"
                        />
                        <Button size="sm" onClick={() => void renameTeam(team.id)}>Valider</Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setRenamingTeamId(null);
                            setRenameValue('');
                          }}
                        >
                          Annuler
                        </Button>
                      </div>
                    ) : (
                      <h3 className="text-sm font-semibold text-gray-900">{team.name}</h3>
                    )}

                    {isTenantManager && renamingTeamId !== team.id && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setRenamingTeamId(team.id);
                            setRenameValue(team.name);
                          }}
                        >
                          Renommer
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => void archiveTeam(team.id)}>
                          Archiver
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 space-y-2">
                    {teamMemberList.map((teamMember) => {
                      const profile = memberByUserId[teamMember.user_id];
                      const name = profile?.display_name || teamMember.user_id.slice(0, 8);

                      return (
                        <div
                          key={teamMember.id}
                          className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm text-gray-900">{name}</p>
                            <p className="text-xs text-gray-500">{teamMember.user_id}</p>
                          </div>

                          <div className="flex items-center gap-2">
                            <Badge variant={teamMember.role === 'manager' ? 'success' : 'outline'}>
                              {teamMember.role === 'manager' ? 'Responsable' : 'Membre'}
                            </Badge>
                            {isTenantManager && (
                              <button
                                onClick={() => void removeMember(teamMember)}
                                className="text-xs text-gray-500 hover:text-red-600"
                              >
                                Retirer
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {isTenantManager && (
                    <div className="mt-3 flex max-w-xl items-center gap-2">
                      <select
                        value={addMemberUserByTeam[team.id] ?? ''}
                        onChange={(event) => {
                          setAddMemberUserByTeam((previous) => ({
                            ...previous,
                            [team.id]: event.target.value,
                          }));
                        }}
                        className="h-9 flex-1 rounded-lg border border-gray-200 px-3 text-sm"
                      >
                        <option value="">Ajouter un membre existant...</option>
                        {availableUsers.map((member) => (
                          <option key={member.user_id} value={member.user_id}>
                            {(member.display_name || member.user_id.slice(0, 8))} ({member.tenant_role})
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        onClick={() => void addMember(team.id)}
                        disabled={!addMemberUserByTeam[team.id]}
                      >
                        Ajouter
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};
